import React, { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import "driver.js/dist/driver.css";
import "./tourTheme.css";
import { hasSeenTour } from "./tourStorage";
import { resolveTourForHelp, resolveTourForPath } from "./tourSteps";
import { startOnboardingTour } from "./buildDriver";

/**
 * Auto-starts first-time dashboard or module tours; floating help replays the current page tour.
 * @param {{ role: "staff" | "member" | "investor" }} props
 */
export default function OnboardingTourHost({ role = "staff" }) {
  const location = useLocation();
  const driverRef = useRef(null);
  const autoStartedRef = useRef(new Set());

  const stopTour = useCallback(() => {
    try {
      driverRef.current?.destroy?.();
    } catch {
      // ignore
    }
    driverRef.current = null;
  }, []);

  const startTourById = useCallback(
    (tourId) => {
      if (!tourId) return;
      if (driverRef.current?.isActive?.()) return;
      stopTour();
      driverRef.current = startOnboardingTour(tourId, {
        onDestroyed: () => {
          driverRef.current = null;
        },
      });
    },
    [stopTour]
  );

  const startHelpTour = useCallback(() => {
    const tour = resolveTourForHelp(role, location.pathname);
    startTourById(tour?.id);
  }, [role, location.pathname, startTourById]);

  useEffect(() => {
    const tour = resolveTourForPath(role, location.pathname);
    if (!tour) return undefined;
    if (hasSeenTour(tour.id) || autoStartedRef.current.has(tour.id)) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (hasSeenTour(tour.id) || autoStartedRef.current.has(tour.id)) return;
      if (driverRef.current?.isActive?.()) return;
      autoStartedRef.current.add(tour.id);
      startTourById(tour.id);
    }, 500);

    return () => window.clearTimeout(timer);
  }, [location.pathname, role, startTourById]);

  useEffect(() => () => stopTour(), [stopTour]);

  return (
    <button
      type="button"
      className="uc-tour-help-btn"
      aria-label="Start guided tour"
      title="Guided tour"
      onClick={startHelpTour}
    >
      ?
    </button>
  );
}
